import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';
import '@diekeure/dcr-components/dcr-tree-item.js';
import '@diekeure/dcr-components/dcr-tree.js';
import { TreeItemDataInterface } from './tree-item.interface';

@Component({
  standalone: false,
  selector: 'campus-tree',
  templateUrl: 'tree.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TreeComponent implements AfterViewInit {
  @HostBinding('class')
  defaultClasses = ['block'];

  @Input() treeItems: TreeItemDataInterface[] = [];
  @Input() itemTrailingActionsTemplate?: TemplateRef<{ $implicit: TreeItemDataInterface }>;
  @Input() scrollSelectedIntoView = false;
  @ContentChild('trailingActions', { static: false }) trailingActionsTemplate?: TemplateRef<{
    $implicit: TreeItemDataInterface;
  }>;

  @Output() selectionChange = new EventEmitter<string>();

  private readonly nativeElement = inject(ElementRef)?.nativeElement as HTMLElement;
  protected readonly treePrefix = 'treeItem-';

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      if (this.scrollSelectedIntoView) {
        this.scrollToSelected();
      }
    });
  }

  trackByFn(index: number, item: TreeItemDataInterface) {
    return item.id;
  }

  onSelectionChange(event: { detail: { selectedItem: HTMLElement } }) {
    const id: string = event.detail.selectedItem.id;
    const cleanId = id.startsWith(this.treePrefix) ? id.substring(this.treePrefix.length) : id;
    this.selectionChange.emit(cleanId);
  }

  /**
   * Re-applies the `selected` state from `treeItems` onto the rendered items.
   *
   * dcr-tree selects an item the instant it is clicked — before a consumer of
   * `selectionChange` can decide whether to honour it — by setting `selected`
   * directly on the elements. When that selection is rejected (e.g. a cancelled
   * navigation) the data is unchanged, so Angular's bindings never re-write it and
   * the highlight is stranded on the wrong item. Call this to put it back.
   */
  restoreSelection(): void {
    const selectedIds = new Set<string>();
    const collect = (items: TreeItemDataInterface[]): void => {
      for (const item of items) {
        if (item.selected) {
          selectedIds.add(this.treePrefix + item.id);
        }
        if (item.children?.length) {
          collect(item.children);
        }
      }
    };
    collect(this.treeItems);

    this.nativeElement.querySelectorAll<HTMLElement & { selected: boolean }>('dcr-tree-item').forEach((element) => {
      const selected = selectedIds.has(element.id);
      if (element.selected !== selected) {
        element.selected = selected;
      }
    });
  }

  private scrollToSelected() {
    const scrollableSelectedItem = this.findSelectedWithScrollFlag(this.treeItems);
    if (scrollableSelectedItem && scrollableSelectedItem.id) {
      const selectedElement = this.nativeElement.querySelector(`[id="${this.treePrefix}${scrollableSelectedItem.id}"]`);
      if (selectedElement) {
        (selectedElement as HTMLElement).scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    }
  }

  private findSelectedWithScrollFlag(items: TreeItemDataInterface[]): TreeItemDataInterface | null {
    for (const item of items) {
      if (item.selected) {
        return item;
      }
      if (item.children?.length) {
        const found = this.findSelectedWithScrollFlag(item.children);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}
